import { PENDO_VISITOR_METADATA_KEYS } from 'src/app/core/constants/pendo.constants';
import type { UserApiData } from '../user/user-api-data.interface';
import type { PendoVisitorMetadata } from './pendo.types';

const AGE_BANDS = [
  { maxAge: 17, label: 'Under 18' },
  { maxAge: 29, label: '18-29' },
  { maxAge: 45, label: '30-45' },
  { maxAge: 60, label: '46-60' },
  { maxAge: Number.POSITIVE_INFINITY, label: '61+' },
] as const;

function coalesceTrimmedString(...candidates: Array<unknown>): string {
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (trimmed) {
        return trimmed;
      }
    }
  }

  return '';
}

/**
 * Derives an age band from date of birth without exposing the raw DOB to analytics.
 * Returns `undefined` when DOB is missing or unparseable.
 */
export function deriveAgeBandFromDateOfBirth(
  dateOfBirth: string | null | undefined,
): string | undefined {
  if (!dateOfBirth) {
    return undefined;
  }

  const parsed = new Date(dateOfBirth);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  const today = new Date();
  let age = today.getFullYear() - parsed.getFullYear();
  const monthDelta = today.getMonth() - parsed.getMonth();

  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < parsed.getDate())) {
    age -= 1;
  }

  if (age < 0) {
    return undefined;
  }

  return AGE_BANDS.find((band) => age <= band.maxAge)?.label;
}

function deriveCity(data: Pick<UserApiData, 'City'>): string | undefined {
  const city = coalesceTrimmedString(data.City);
  return city || undefined;
}

function derivePostalCode(data: Pick<UserApiData, 'PostalCode'>): string | undefined {
  const postalCode = coalesceTrimmedString(data.PostalCode);
  return postalCode || undefined;
}

function deriveState(data: Pick<UserApiData, 'State'>): string | undefined {
  const state = coalesceTrimmedString(data.State);
  return state || undefined;
}

function deriveCountry(
  data: Pick<UserApiData, 'Country'>,
  marketFallback?: string,
): string | undefined {
  const country = coalesceTrimmedString(data.Country);
  if (country) {
    return country;
  }

  const fallback = coalesceTrimmedString(marketFallback);
  return fallback || undefined;
}

/**
 * Builds Pendo visitor metadata from user API data.
 * Identity is the opaque `UserKey` only — no PII is included.
 * Includes the environment as a filterable visitor attribute.
 */
export function buildPendoVisitorMetadata(
  data: UserApiData,
  marketFallback?: string,
  environmentName?: string,
): PendoVisitorMetadata | null {
  const userToken = coalesceTrimmedString(data.UserKey);
  if (!userToken) {
    return null;
  }

  const metadata: PendoVisitorMetadata = {
    id: userToken,
  };

  const ageBand = deriveAgeBandFromDateOfBirth(data.DateOfBirth);
  if (ageBand) {
    metadata[PENDO_VISITOR_METADATA_KEYS.ageBand] = ageBand;
  }

  const city = deriveCity(data);
  if (city) {
    metadata[PENDO_VISITOR_METADATA_KEYS.city] = city;
  }

  const postalCode = derivePostalCode(data);
  if (postalCode) {
    metadata[PENDO_VISITOR_METADATA_KEYS.postalCode] = postalCode;
  }

  const state = deriveState(data);
  if (state) {
    metadata[PENDO_VISITOR_METADATA_KEYS.state] = state;
  }

  const country = deriveCountry(data, marketFallback);
  if (country) {
    metadata[PENDO_VISITOR_METADATA_KEYS.country] = country;
  }

  const partyType = coalesceTrimmedString(data.UserType);
  if (partyType) {
    metadata[PENDO_VISITOR_METADATA_KEYS.partyType] = partyType;
  }

  const environment = coalesceTrimmedString(environmentName);
  if (environment) {
    metadata[PENDO_VISITOR_METADATA_KEYS.environment] = environment;
  }

  return metadata;
}
